import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import TextField from '@mui/material/TextField';
import { GridColDef } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { FilterableDataTable, DrawerPanel, ConfirmDialog } from '../../components/shared';
import type { RowAction, DrawerAction } from '../../components/shared';
import { ToolControllerApi } from '../../api-generated/apis';
import { apiConfig } from '../../api-generated/apiConfig';

interface Tool {
  id: string;
  name: string;
  description: string;
  partNumber: string;
}

const toolApi = new ToolControllerApi(apiConfig);

const columns: GridColDef[] = [
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
  { field: 'description', headerName: 'Description', flex: 1.5, minWidth: 200 },
  { field: 'partNumber', headerName: 'Part Number', flex: 0.8, minWidth: 120 },
];

async function fetchTools(): Promise<Tool[]> {
  return toolApi.getAll() as any;
}

async function saveTool(tool: Partial<Tool>, isEdit: boolean): Promise<Tool> {
  if (isEdit) {
    return toolApi.update({ id: tool.id!, tool: tool as any }) as any;
  }
  return toolApi.create({ tool: tool as any }) as any;
}

async function deleteTool(id: string): Promise<void> {
  return toolApi._delete({ id });
}

const ToolsPanel: React.FC = () => {
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingTool, setEditingTool] = useState<Tool | null>(null);
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formPartNumber, setFormPartNumber] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ['tools'], queryFn: fetchTools });

  const saveMutation = useMutation({
    mutationFn: () => saveTool({ id: editingTool?.id, name: formName, description: formDescription, partNumber: formPartNumber }, !!editingTool),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['tools'] }); setDrawerOpen(false); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteTool(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['tools'] }); setConfirmOpen(false); },
  });

  const handleAdd = useCallback(() => {
    setEditingTool(null); setFormName(''); setFormDescription(''); setFormPartNumber(''); setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row: Tool) => {
    setEditingTool(row); setFormName(row.name); setFormDescription(row.description); setFormPartNumber(row.partNumber || ''); setDrawerOpen(true);
  }, []);

  const rowActions: RowAction<Tool>[] = [
    { label: 'Edit', icon: EditIcon, onClick: handleEdit },
    { label: 'Delete', icon: DeleteIcon, onClick: (row) => { setDeleteId(row.id); setConfirmOpen(true); } },
  ];

  const drawerActions: DrawerAction[] = [
    { label: 'Cancel', variant: 'outlined', onClick: () => setDrawerOpen(false) },
    { label: editingTool ? 'Update' : 'Create', variant: 'contained', onClick: () => saveMutation.mutate(), disabled: !formName },
  ];

  return (
    <>
      <FilterableDataTable<Tool>
        rows={data ?? []}
        columns={columns}
        loading={isLoading}
        totalCount={data?.length ?? 0}
        page={0}
        pageSize={25}
        onPageChange={() => {}}
        onPageSizeChange={() => {}}
        actions={rowActions}
        primaryAction={{ label: 'Add Tool', onClick: handleAdd }}
        searchPlaceholder="Search tools..."
      />
      <DrawerPanel open={drawerOpen} onClose={() => setDrawerOpen(false)} title={editingTool ? 'Edit Tool' : 'New Tool'} width="narrow" actions={drawerActions}>
        <TextField label="Name" value={formName} onChange={(e) => setFormName(e.target.value)} fullWidth size="small" sx={{ mb: 2 }} />
        <TextField label="Description" value={formDescription} onChange={(e) => setFormDescription(e.target.value)} fullWidth size="small" multiline rows={2} sx={{ mb: 2 }} />
        <TextField label="Part Number" value={formPartNumber} onChange={(e) => setFormPartNumber(e.target.value)} fullWidth size="small" />
      </DrawerPanel>
      <ConfirmDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} onConfirm={() => deleteId && deleteMutation.mutate(deleteId)} title="Delete Tool" message="Are you sure?" confirmLabel="Delete" confirmColor="error" />
    </>
  );
};

export default ToolsPanel;
